import { Navbar } from "@/components/layout/Navbar"
import { Footer } from "@/components/layout/Footer"
import { toast } from "sonner"

import { useState } from "react"
import { useLocation } from "react-router-dom"
import { useAuthStore } from "@/stores/auth.store"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"

import { AmbientBackground } from "@/components/ui/ambient-background"
import { Icon } from "@/components/ui/icon"

const RETURN_AFTER_LOGIN_KEY = "LIZTA_RETURN_AFTER_LOGIN"

export default function LoginPage() {
  const location = useLocation()
  const { signInWithMagicLink, signInWithGoogle } = useAuthStore()
  const [email, setEmail] = useState("")
  const [loadingMagicLink, setLoadingMagicLink] = useState(false)
  const [loadingGoogle, setLoadingGoogle] = useState(false)

  const returnTo =
    (location.state as { from?: string })?.from ||
    (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(RETURN_AFTER_LOGIN_KEY) : null) ||
    ""
  const callbackUrl = `${window.location.origin}/auth/callback${returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : ""}`

  const handleMagicLink = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoadingMagicLink(true)

    try {
      await signInWithMagicLink(email, callbackUrl)
      toast.success("Acabamos de enviar um link para seu e-mail. Clique nele para acessar sua conta")
      setEmail("")
    } catch (err: any) {
      toast.error(err.message || "Falha ao enviar link de acesso")
    } finally {
      setLoadingMagicLink(false)
    }
  }

  const handleGoogleSignIn = async () => {
    setLoadingGoogle(true)

    try {
      await signInWithGoogle(callbackUrl)
    } catch (err: any) {
      toast.error(err.message || "Falha ao iniciar autenticação com Google")
      setLoadingGoogle(false)
    }
  }

  return (
    <div className="min-h-screen w-full bg-background overflow-hidden relative">
      {/* Background Blobs - Soft Pop Vibes */}
      <AmbientBackground  />
      
      <div className="relative z-10 min-h-screen flex flex-col">
        <Navbar  hideLoginButton />

        <main className="flex-1 flex items-center justify-center p-6 lg:p-10 min-h-[calc(98vh-4rem)]">
          <div className="w-full max-w-6xl flex flex-col lg:grid lg:grid-cols-2 gap-10 lg:gap-20 items-center">
            
            {/* Mobile: top | Desktop: left — Typography & Brand */}
            <div className="flex flex-col gap-6 text-center lg:text-left order-1">
              <div className="space-y-2">
                <span className="inline-block px-4 py-1.5 rounded-full bg-primary/10 text-primary font-bold text-sm tracking-wide uppercase">
                  Bem-vindo de volta
                </span>
                <h1 className="text-5xl lg:text-7xl font-black text-foreground tracking-tight leading-[1.1]">
                  Gerencie sua equipe <span className="text-primary">fácil.</span>
                </h1>
              </div>
              <p className="text-xl text-muted-foreground leading-relaxed max-w-lg mx-auto lg:mx-0">
                Organize jogos, pagamentos e estatísticas sem dor de cabeça. O jeito moderno de administrar seu time.
              </p>
            </div>

            {/* Mobile: below | Desktop: right — Login Form */}
            <div className="w-full max-w-md mx-auto order-2">
              <div className="bg-card-surface/80 backdrop-blur-xl border border-white/50 dark:border-white/10 p-8 rounded-xl shadow-2xl shadow-primary/10 ring-1 ring-black/5">
                <div className="flex flex-col gap-1 mb-6">
                  <h2 className="text-2xl font-bold text-foreground">Acessar conta</h2>
                  <p className="text-muted-foreground">Entre com Google ou email.</p>
                </div>
                
                <Button 
                  onClick={handleGoogleSignIn}
                  loading={loadingGoogle}
                  disabled={loadingMagicLink || loadingGoogle}
                  variant="outline"
                  className="w-full h-14 rounded-xl border-2 border-border hover:bg-secondary/50 hover:border-primary/20 gap-3 text-base"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                  </svg>
                  Continuar com Google
                </Button>

                <div className="relative my-8">
                  <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-border"></div></div>
                  <div className="relative flex justify-center text-xs uppercase"><span className="bg-card-surface px-2 text-muted-foreground font-semibold">Ou continue com</span></div>
                </div>

                <form onSubmit={handleMagicLink} className="flex flex-col gap-5">
                  <div className="space-y-2">
                    <label className="text-sm font-semibold text-foreground ml-1">E-mail</label>
                    <div className="relative group">
                      <Input 
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        disabled={loadingMagicLink || loadingGoogle}
                        className="w-full h-14 pl-5 pr-12 rounded-xl bg-input-bg border-2 focus:border-primary/20 focus:bg-white dark:focus:bg-black/20 text-foreground transition-all placeholder:text-muted-foreground/50 font-medium py-0"
                        placeholder="nome@exemplo.com" 
                        type="email" 
                        required
                      />
                      <Icon name="mail" />
                    </div>
                  </div>
                  
                  <Button 
                    type="submit" 
                    loading={loadingMagicLink}
                    disabled={loadingMagicLink || loadingGoogle}
                    className="w-full h-14 text-lg rounded-xl shadow-lg shadow-primary/25 hover:shadow-primary/40 hover:-translate-y-0.5 active:translate-y-0 transition-all duration-300"
                  >
                    {loadingMagicLink ? "Enviando..." : "Continuar com email"}
                  </Button>
                </form>
              </div>
            </div>
          </div>
        </main>
        
        <div className="relative z-10">
          <Footer />
        </div>
      </div>
    </div>
  )
}
